import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Checkbox, Collapse, Divider, FormControlLabel, Grid, InputAdornment, Stack, Typography, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { Button } from "Components/_Shared/Buttons/Button";
import { DialogModal } from "Components/_Shared/DialogModal";
import { ReactComponent as AddIcon } from "Components/_Shared/Icons/Assets/add.svg";
import { ReactComponent as BinIcon } from "Components/_Shared/Icons/Assets/bin.svg";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { FormDropdown, type Option } from "Forms/Components/FormDropdown";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import {
  type FinancialProductExpenseCustomItem,
  type FinancialProductExpenseFormType,
  getFinancialProductExpenseFormSchema,
} from "Forms/Schemas/Budget/Expenses/financialProductExpenseFormSchema";
import {
  getGetBudgetOverviewQueryKey,
  getGetFinancialProductExpenseQueryKey,
  useCreateFinancialProductExpenseItem,
  useDeleteFinancialProductExpenseItem,
  useGetAnalysisClients,
  useGetFinancialProductExpense,
  useUpdateFinancialProductExpenseItem,
  useUpdateFinancialProductExpenseSettings,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  AmountFrequency,
  type FinancialProductExpenseItemBody,
  FinancialProductExpenseItemFinancialProductType,
} from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatClientName } from "Infrastructure/Utils/ClientUtils";
import { Fragment, type RefObject, forwardRef, useContext, useEffect, useImperativeHandle, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import type { ExpenseFormHandle } from "../Components/EditExpenseDrawer";
import RotatingArrowButton from "../Components/RotatingArrowButton";

type Props = {
  onClose: () => void;
  ref?: RefObject<ExpenseFormHandle>;
};

type deleteExpenseRequest = {
  expenseItemID: string;
  relatedAssetItemID?: string | null;
};

type deleteExpenseBulkRequest = {
  hasRelatedAssetItem: boolean;
};

const emptyCustomItem: FinancialProductExpenseCustomItem = {
  expenseItemID: null,
  type: FinancialProductExpenseItemFinancialProductType.building_saving,
  installment: "",
  balance: "",
  institution: "",
  belongsTo: "",
  relatedAssetItemID: null,
  id: "",
};

export const FinancialProductExpenseForm = forwardRef<ExpenseFormHandle, Props>(({ onClose }, ref) => {
  const { readOnly } = useContext(AnalysisDetailContext);

  const { t } = useResource();
  const theme = useTheme();
  const [detailed, setDetailed] = useState(true);
  const [openDeleteModal, setOpenDeleteModal] = useState<deleteExpenseRequest | null>(null);
  const [openDeleteBulkModal, setOpenDeleteBulkModal] = useState<deleteExpenseBulkRequest | null>(null);
  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };

  const queryClient = useQueryClient();
  const { data: financialProductExpense } = useGetFinancialProductExpense(analysisId, versionId);
  const { data: clientsData } = useGetAnalysisClients(analysisId, versionId);
  const { mutateAsync: createFinancialProductExpenseItemAsync } = useCreateFinancialProductExpenseItem();
  const { mutateAsync: updateFinancialProductExpenseItemAsync } = useUpdateFinancialProductExpenseItem();
  const { mutate: deleteFinancialProductExpenseItem, mutateAsync: deleteFinancialProductExpenseItemAsync } =
    useDeleteFinancialProductExpenseItem();
  const { mutateAsync: updateFinancialProductExpenseSettingsAsync } = useUpdateFinancialProductExpenseSettings();

  const belongsToOptions: Option[] = [
    { name: "-", value: "" },
    ...(clientsData?.clients ?? []).map((c) => ({
      name: formatClientName(c.firstName, c.surname, c.birthDate ?? null),
      value: c.guid,
    })),
  ];
  const financialProductExpenseTypeOptions: Option[] = [
    {
      name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.FinancialProductExpenseTypes.BuildingSaving),
      value: FinancialProductExpenseItemFinancialProductType.building_saving,
    },
    {
      name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.FinancialProductExpenseTypes.PensionSaving),
      value: FinancialProductExpenseItemFinancialProductType.pension_saving,
    },
    {
      name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.FinancialProductExpenseTypes.SavingAccount),
      value: FinancialProductExpenseItemFinancialProductType.saving_account,
    },
    {
      name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.FinancialProductExpenseTypes.Investment),
      value: FinancialProductExpenseItemFinancialProductType.investment,
    },
    {
      name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.FinancialProductExpenseTypes.RealEstateFund),
      value: FinancialProductExpenseItemFinancialProductType.real_estate_fund,
    },
    {
      name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.FinancialProductExpenseTypes.Commodities),
      value: FinancialProductExpenseItemFinancialProductType.commodities,
    },
    {
      name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.FinancialProductExpenseTypes.Platform),
      value: FinancialProductExpenseItemFinancialProductType.platform,
    },
    {
      name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.FinancialProductExpenseTypes.Other),
      value: FinancialProductExpenseItemFinancialProductType.other,
    },
  ];

  const {
    control,
    setValue,
    watch,
    formState: { errors, dirtyFields },
    handleSubmit,
    clearErrors,
  } = useForm<FinancialProductExpenseFormType>({
    resolver: yupResolver(getFinancialProductExpenseFormSchema(t)),
    mode: "onBlur",
    defaultValues: {
      aggregatedAmount: "",
      customItems: [],
      detailed: true,
    },
  });

  const {
    fields: customFields,
    append,
    remove,
    replace,
  } = useFieldArray({
    control,
    name: "customItems",
  });

  const customItems = watch("customItems");

  useEffect(() => {
    if (detailed && customItems) {
      const total = customItems.reduce((sum, item) => {
        const value = Number.parseFloat(item.installment ?? "0") || 0;
        return sum + value;
      }, 0);
      setValue("aggregatedAmount", total.toString());
    }
  }, [customItems, detailed, setValue]);

  const recalculateTotal = () => {
    if (detailed && customItems) {
      const total = customItems.reduce((sum, item) => {
        const value = Number.parseFloat(item.installment ?? "0") || 0;
        return sum + value;
      }, 0);
      setValue("aggregatedAmount", total.toString());
    }
  };

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      handleSubmit(onSubmit)();
    },
    deleteExpense: () => {
      const hasRelatedAssetItem = customFields.some((field) => field.relatedAssetItemID);
      setOpenDeleteBulkModal({ hasRelatedAssetItem });
    },
  }));

  const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>({});
  const toggle = (id: string) => {
    setExpandedMap((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  useEffect(() => {
    if (financialProductExpense) {
      handleChangeDetailed(financialProductExpense.entryMode === "individual");
      setValue("aggregatedAmount", (financialProductExpense.aggregatedAmount?.value ?? 0).toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financialProductExpense, setValue]);

  useEffect(() => {
    if ((dirtyFields.customItems ?? []).some(Boolean)) {
      return;
    }
    const items = (financialProductExpense?.items ?? []).map(
      (item): FinancialProductExpenseCustomItem => ({
        expenseItemID: item.guid,
        type: item.financialProductType,
        installment: item.amount.value.toString(),
        balance: item.balance.value.toString(),
        institution: item.institution ?? "",
        belongsTo: item.clientGuid ?? "",
        relatedAssetItemID: item.relatedAssetItemGuid ?? null,
      }),
    );
    replace(items.length > 0 ? items : [{ ...emptyCustomItem }]);
  }, [financialProductExpense?.items, replace, dirtyFields.customItems]);

  const handleChangeDetailed = (value: boolean) => {
    setDetailed(value);
    setValue("detailed", value);
    clearErrors();
  };

  const buildFinancialProductExpenseItemBody = (field: FinancialProductExpenseCustomItem): FinancialProductExpenseItemBody => ({
    name: financialProductExpenseTypeOptions.find((o) => o.value === field.type)?.name || "",
    financialProductType:
      (field.type as FinancialProductExpenseItemFinancialProductType) || FinancialProductExpenseItemFinancialProductType.building_saving,
    amount: {
      value: Number.parseFloat(field.installment ?? "0") || 0,
      currencyCode: "CZK",
      frequency: AmountFrequency.monthly,
    },
    balance: {
      value: Number.parseFloat(field.balance ?? "0") || 0,
      currencyCode: "CZK",
      frequency: AmountFrequency.one_time,
    },
    institution: field.institution ?? "",
    clientGuid: field.belongsTo || undefined,
    relatedAssetItemGuid: field.relatedAssetItemID || undefined,
  });

  const invalidateFinancialProductExpenseQueries = () => {
    queryClient.invalidateQueries({ queryKey: getGetFinancialProductExpenseQueryKey(analysisId, versionId) });
    queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) });
  };

  const onSubmit = async (data: FinancialProductExpenseFormType) => {
    try {
      const currentItems = detailed ? (data.customItems ?? []) : [];
      const currentGuids = new Set(currentItems.filter((item) => item.expenseItemID).map((item) => item.expenseItemID as string));

      const toCreate = currentItems.filter((item) => !item.expenseItemID);
      const toUpdate = currentItems.filter((item) => item.expenseItemID);
      const toDelete = (financialProductExpense?.items ?? []).filter((item) => !currentGuids.has(item.guid));

      await Promise.all([
        ...toCreate.map((item) =>
          createFinancialProductExpenseItemAsync({ analysisId, versionId, data: buildFinancialProductExpenseItemBody(item) }),
        ),
        ...toUpdate.map((item) =>
          updateFinancialProductExpenseItemAsync({
            analysisId,
            versionId,
            itemId: item.expenseItemID as string,
            data: buildFinancialProductExpenseItemBody(item),
          }),
        ),
        ...toDelete.map((item) =>
          deleteFinancialProductExpenseItemAsync({
            analysisId,
            versionId,
            itemId: item.guid,
          }),
        ),
      ]);

      await updateFinancialProductExpenseSettingsAsync({
        analysisId,
        versionId,
        data: {
          entryMode: detailed ? "individual" : "aggregate",
          aggregatedAmount: {
            value: Number.parseFloat(data.aggregatedAmount ?? "0") || 0,
            currencyCode: "CZK",
            frequency: AmountFrequency.monthly,
          },
        },
      });

      invalidateFinancialProductExpenseQueries();
      onClose();
    } catch (_error) {
      toast.error(t(Resources.AnalysisDetail.Budget.Expenses.Form.Errors.ExpenseSubmitError));
    }
  };

  const onDeleteBulkExpense = async (deleteWithRelatedAssetItem: boolean) => {
    try {
      for (const field of customFields) {
        if (!field.expenseItemID) continue;
        const params =
          deleteWithRelatedAssetItem && field.relatedAssetItemID ? { relatedAssetItemGuid: field.relatedAssetItemID } : undefined;
        await deleteFinancialProductExpenseItemAsync({ analysisId, versionId, itemId: field.expenseItemID, params });
      }
      replace([{ ...emptyCustomItem }]);

      await updateFinancialProductExpenseSettingsAsync({
        analysisId,
        versionId,
        data: {
          entryMode: detailed ? "individual" : "aggregate",
          aggregatedAmount: { value: 0, currencyCode: "CZK", frequency: AmountFrequency.monthly },
        },
      });

      setOpenDeleteBulkModal(null);
      invalidateFinancialProductExpenseQueries();
      onClose();
    } catch (_error) {
      toast.error(t(Resources.AnalysisDetail.Budget.Expenses.Form.Errors.ExpenseSubmitError));
    }
  };

  const onDeleteExpenseItem = (expenseItemID: string, relatedAssetItemID?: string | null) => {
    const isLastItem = customFields.length === 1;
    const remainingTotal = customFields
      .filter((item) => item.expenseItemID !== expenseItemID)
      .reduce((sum, item) => sum + (Number.parseFloat(item.installment ?? "0") || 0), 0);

    deleteFinancialProductExpenseItem(
      {
        analysisId,
        versionId,
        itemId: expenseItemID,
        params: relatedAssetItemID ? { relatedAssetItemGuid: relatedAssetItemID } : undefined,
      },
      {
        onSuccess: async () => {
          remove(customFields.findIndex((item) => item.expenseItemID === expenseItemID));
          setOpenDeleteModal(null);

          try {
            await updateFinancialProductExpenseSettingsAsync({
              analysisId,
              versionId,
              data: {
                entryMode: "individual",
                aggregatedAmount: { value: remainingTotal, currencyCode: "CZK", frequency: AmountFrequency.monthly },
              },
            });
          } catch {
            toast.error(t(Resources.AnalysisDetail.Budget.Expenses.Form.Errors.ExpenseSubmitError));
          }

          invalidateFinancialProductExpenseQueries();
          if (isLastItem) {
            onClose();
          }
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Budget.Expenses.Form.Errors.ExpenseSubmitError));
        },
      },
    );
  };

  const deleteExpenseItem = (expenseItem: FinancialProductExpenseCustomItem) => {
    if (expenseItem.expenseItemID) {
      setOpenDeleteModal({
        expenseItemID: expenseItem.expenseItemID,
        relatedAssetItemID: expenseItem.relatedAssetItemID,
      });
      return;
    }
    remove(customFields.findIndex((item) => item.id === expenseItem.id));
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={Spacing.XXL} pb={Spacing.XXL} sx={{ height: "100%" }}>
        <Box>
          <Typography fontWeight={600} variant="body1" mb={Spacing.XL}>
            {t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.FinancialProduct)}
          </Typography>

          <Grid container spacing={Spacing.XL}>
            <Grid size={6}>
              <BlFormInput
                control={control}
                name="aggregatedAmount"
                label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Fields.Aggregated)}
                errors={errors}
                disabled={detailed || readOnly}
                inputEndAdornment={
                  <InputAdornment position="end">{`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}</InputAdornment>
                }
                mask={[
                  {
                    mask: Number,
                    thousandsSeparator: " ",
                    scale: 2,
                    min: 0,
                  },
                ]}
                placeholder="0"
              />
            </Grid>

            <Grid size={6}>
              <Box display="flex" alignItems="flex-start" mt={5} height="100%">
                <FormControlLabel
                  control={<Checkbox checked={detailed} disabled={readOnly} onChange={(_, checked) => handleChangeDetailed(checked)} />}
                  label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Fields.DetailedCheckbox)}
                />
              </Box>
            </Grid>

            {detailed && (
              <>
                {customFields.map((field, idx) => (
                  <Fragment key={field.id}>
                    <Grid size={12} container spacing={Spacing.L}>
                      <Grid size={10}>
                        <FormDropdown
                          control={control}
                          disabled={readOnly}
                          name={`customItems.${idx}.type`}
                          options={financialProductExpenseTypeOptions}
                          label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Labels.Item)}
                          errors={errors}
                          labelEndAdornment={<StyledLabelAdornment />}
                        />
                      </Grid>

                      <Grid size={1} display="flex" alignItems="center" justifyContent="center" mt={Spacing.XL}>
                        <Button disabled={readOnly} variant="text" onClick={() => deleteExpenseItem(field)}>
                          <BinIcon />
                        </Button>
                      </Grid>

                      <Grid size={1} display="flex" alignItems="center" justifyContent="center" mt={Spacing.XL}>
                        <RotatingArrowButton onClick={() => toggle(field.id)} />
                      </Grid>

                      <Grid size={6}>
                        <BlFormInput
                          control={control}
                          disabled={readOnly}
                          name={`customItems.${idx}.installment`}
                          label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Labels.MonthlySaving)}
                          errors={errors}
                          labelEndAdornment={<StyledLabelAdornment />}
                          inputEndAdornment={
                            <InputAdornment position="end">{`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}</InputAdornment>
                          }
                          mask={[
                            {
                              mask: Number,
                              thousandsSeparator: " ",
                              scale: 2,
                              min: 0,
                            },
                          ]}
                          placeholder="0"
                          onChange={recalculateTotal}
                        />
                      </Grid>

                      <Grid size={6}>
                        <BlFormInput
                          control={control}
                          disabled={readOnly}
                          name={`customItems.${idx}.balance`}
                          label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Labels.Balance)}
                          errors={errors}
                          inputEndAdornment={<InputAdornment position="end">{t(Resources.Common.CZKSuffix)}</InputAdornment>}
                          mask={[
                            {
                              mask: Number,
                              thousandsSeparator: " ",
                              scale: 2,
                              min: 0,
                            },
                          ]}
                          placeholder="0"
                        />
                      </Grid>

                      <Collapse in={!!expandedMap[field.id]} sx={{ width: "100%" }}>
                        <Grid size={12} container spacing={Spacing.L} alignItems="center">
                          <Grid size={6}>
                            <BlFormInput
                              control={control}
                              disabled={readOnly}
                              name={`customItems.${idx}.institution`}
                              label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Labels.Institution)}
                              errors={errors}
                            />
                          </Grid>

                          <Grid size={6}>
                            <FormDropdown
                              control={control}
                              disabled={readOnly}
                              name={`customItems.${idx}.belongsTo`}
                              options={belongsToOptions}
                              label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Labels.BelongsTo)}
                              errors={errors}
                              hasValidationText={false}
                            />
                          </Grid>
                        </Grid>
                      </Collapse>
                    </Grid>
                    <Divider style={{ width: "100%" }} />
                  </Fragment>
                ))}

                <Grid size={12}>
                  <Button
                    variant="text"
                    disabled={readOnly}
                    colorCustom={theme.colors.brandPrimary}
                    startAdornment={<AddIcon />}
                    onClick={() => append({ ...emptyCustomItem })}
                  >
                    {t(Resources.AnalysisDetail.Budget.Expenses.Form.Buttons.AddCustomItem)}
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Stack>

      <DialogModal
        isOpen={!!openDeleteModal}
        onClose={() => setOpenDeleteModal(null)}
        title={t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteExpenseItem)}
        subtitle={t(
          openDeleteModal?.relatedAssetItemID
            ? Resources.AnalysisDetail.Budget.Expenses.Form.DeleteFinancialProductExpenseItemWithAssetHint
            : Resources.AnalysisDetail.Budget.Expenses.Form.DeleteFinancialProductExpenseItemWithoutAssetHint,
        )}
        primaryAction={
          openDeleteModal?.relatedAssetItemID ? (
            <Button
              disabled={readOnly}
              onClick={() => onDeleteExpenseItem(openDeleteModal?.expenseItemID || "", openDeleteModal?.relatedAssetItemID)}
            >
              {t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteFinancialProductExpenseItemWithAssetSubmit)}
            </Button>
          ) : (
            <Button disabled={readOnly} onClick={() => onDeleteExpenseItem(openDeleteModal?.expenseItemID || "")}>
              {t(Resources.Common.Yes)}
            </Button>
          )
        }
        secondaryAction={
          openDeleteModal?.relatedAssetItemID ? (
            <Button disabled={readOnly} onClick={() => onDeleteExpenseItem(openDeleteModal?.expenseItemID || "")}>
              {t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteFinancialProductExpenseItemWithoutAssetSubmit)}
            </Button>
          ) : undefined
        }
      />

      <DialogModal
        isOpen={!!openDeleteBulkModal}
        onClose={() => setOpenDeleteBulkModal(null)}
        title={t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteExpense)}
        subtitle={t(
          openDeleteBulkModal?.hasRelatedAssetItem
            ? Resources.AnalysisDetail.Budget.Expenses.Form.DeleteFinancialProductExpenseWithAssetHint
            : Resources.AnalysisDetail.Budget.Expenses.Form.DeleteExpenseHint,
        )}
        primaryAction={
          openDeleteBulkModal?.hasRelatedAssetItem ? (
            <Button disabled={readOnly} onClick={() => onDeleteBulkExpense(true)}>
              {t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteFinancialProductExpenseItemWithAssetSubmit)}
            </Button>
          ) : (
            <Button disabled={readOnly} onClick={() => onDeleteBulkExpense(false)}>
              {t(Resources.Common.Yes)}
            </Button>
          )
        }
        secondaryAction={
          openDeleteBulkModal?.hasRelatedAssetItem ? (
            <Button disabled={readOnly} onClick={() => onDeleteBulkExpense(false)}>
              {t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteFinancialProductExpenseItemWithoutAssetSubmit)}
            </Button>
          ) : undefined
        }
      />
    </form>
  );
});
